import axios from "axios";
import { CognitoUserPool } from "amazon-cognito-identity-js";
import { API_BASE } from "../utils/api";

// Self-service account management.
//
// The password lives in Cognito, so that operation runs against the user's own
// Cognito session; name and phone live in Django. Changing the sign-in email is
// deliberately not offered here — it is the Cognito identity the API resolves
// the Django user from, so it is an administrator action.

const userPool = new CognitoUserPool({
  UserPoolId: process.env.REACT_APP_COGNITO_USER_POOL_ID || "",
  ClientId: process.env.REACT_APP_COGNITO_CLIENT_ID || "",
});

const currentUser = () => userPool.getCurrentUser();

// Cognito operations require a live session to be hydrated first.
function withSession<T>(
  run: (user: ReturnType<typeof currentUser> & object) => Promise<T>,
): Promise<T> {
  return new Promise((resolve, reject) => {
    const user = currentUser();
    if (!user) {
      reject("Your session has expired. Please sign in again.");
      return;
    }
    user.getSession((err: Error | null, session: any) => {
      if (err || !session?.isValid?.()) {
        reject("Your session has expired. Please sign in again.");
        return;
      }
      run(user as any).then(resolve, reject);
    });
  });
}

export interface ProfileUpdate {
  first_name?: string;
  last_name?: string;
  phone_number?: string;
}

// Cognito's changePassword reports a wrong current password as
// NotAuthorizedException / "Incorrect username or password." On this screen the
// username is not in question — the user is already signed in — so echoing that
// verbatim sends people looking for a username problem that doesn't exist.
export function describePasswordError(err: any): string {
  const code = err?.code || err?.name;
  const message: string = err?.message || "";

  if (code === "NotAuthorizedException" || /incorrect username or password/i.test(message)) {
    return "Your current password is incorrect.";
  }
  if (code === "LimitExceededException") {
    return "Too many attempts. Please wait a few minutes and try again.";
  }
  if (code === "InvalidPasswordException") {
    return message || "That password does not meet the password policy.";
  }
  return message || "Could not change the password.";
}

const accountService = {
  async updateProfile(data: ProfileUpdate) {
    const response = await axios.patch(`${API_BASE}/users/me/profile/`, data);
    return response.data;
  },

  changePassword(oldPassword: string, newPassword: string): Promise<void> {
    return withSession(
      (user) =>
        new Promise<void>((resolve, reject) => {
          user.changePassword(oldPassword, newPassword, (err) => {
            if (err) {
              reject(describePasswordError(err));
              return;
            }
            resolve();
          });
        }),
    );
  },

};

export default accountService;
